import type { Student } from "../types/database";

/** Sidebar footer line: "University · Undergraduate" (mock `sb-foot-role`). */
export function studentPortalRoleLine(student: Student | null | undefined): string {
  if (!student) return "Student";
  const uni = student.university?.trim() || "University not set";
  const pl = student.program_level?.trim();
  if (pl) return `${uni} · ${pl}`;
  const yis = student.year_in_school;
  let level = "Student";
  if (yis === "graduate") level = "Graduate";
  else if (yis === "freshman" || yis === "sophomore" || yis === "junior" || yis === "senior") level = "Undergraduate";
  else if (student.graduation_year != null) level = `Class of ${student.graduation_year}`;
  return `${uni} · ${level}`;
}

export function firstNameFromDisplay(displayName: string): string {
  const t = displayName.trim().split(/\s+/)[0];
  return t || "there";
}

/** Single source of truth for profile-completeness fields and labels. */
export type ProfileField = {
  key: string;
  label: string;
  filled: boolean;
};

export function profileFields(student: Student | null | undefined): ProfileField[] {
  if (!student) return [];
  return [
    { key: "university", label: "University", filled: Boolean(student.university?.trim()) },
    { key: "major", label: "Major", filled: Boolean(student.major?.trim()) },
    { key: "graduation_year", label: "Graduation year", filled: student.graduation_year != null },
    {
      key: "program_level",
      label: "Year in school",
      filled: Boolean(student.program_level?.trim()) || student.year_in_school != null,
    },
    { key: "bio", label: "Bio", filled: Boolean(student.bio?.trim()) },
    { key: "resume_url", label: "Resume", filled: Boolean(student.resume_url?.trim()) },
    { key: "linkedin_url", label: "LinkedIn", filled: Boolean(student.linkedin_url?.trim()) },
    { key: "phone", label: "Phone", filled: Boolean(student.phone?.trim()) },
  ];
}

/** Profile strength for dashboard cards — uses only `students` row fields (no extra fetches). */
export function profileCompletenessPercent(student: Student | null | undefined): number {
  if (!student) return 0;
  const fields = profileFields(student);
  if (fields.length === 0) return 0;
  const filled = fields.filter((f) => f.filled).length;
  return Math.round((filled / fields.length) * 100);
}

export function missingProfileFields(student: Student | null | undefined): ProfileField[] {
  return profileFields(student).filter((f) => !f.filled);
}
